import { getBackendUrl } from "@Utils";
import * as Sentry from "@sentry/react";
// Via the alias, not "../axiosWrapper": this module is also bundled by mobile, where
// @AxiosWrapper resolves to mobile's own wrapper. Importing the web one relatively drags
// web's ProviderApp/App/Router into the Metro bundle and uses the wrong store there.
import axiosWrapper from "@AxiosWrapper";
import { resolveFileVersionIds } from "./resolveFileVersionIds";
import type { IsFile403Response } from "@ServerTypes/folders/folders.schema";

/**
 * The stored isFile403 result. `Partial` only because the redux slice starts empty
 * — the shape itself is the server's response schema, which is the sole source of
 * truth for it.
 */
export type FileAccessResult = Partial<IsFile403Response>;

// Transmittal-granted access lives in transmittalResourceHasPermission keyed by the
// transmittal's destination space, so both ids must come from the viewer's URL.
function getTransmittalContext() {
  const params = new URLSearchParams(window.location.search);
  const transmittalId = params.get("transmittalId");
  const spaceId = params.get("spaceId");
  return transmittalId ? { transmittalId, spaceId: spaceId ?? undefined } : undefined;
}

/**
 * THE isFile403 call — reports the caller's permission map, whether the file is
 * deleted, and the same per constituent for a merged file.
 *
 * Issued once per file open, by the setAccessPermission thunk only. The result is
 * stored in the accessPermission slice and every other consumer reads it from
 * there, so there is deliberately no cache here.
 */
export async function resolveFileAccess(
  filesData: Record<string, any> | undefined,
  fileVersionId: string,
): Promise<IsFile403Response> {
  const transmittal = getTransmittalContext();
  const spaceId = transmittal?.spaceId ?? filesData?.[fileVersionId]?.spaceId;
  if (!fileVersionId || !spaceId) return { status: false, errorMessage: "fileVersionId or spaceId missing" };

  try {
    const response = await axiosWrapper.post<IsFile403Response>(`${getBackendUrl()}/folders/isFile403`, {
      fileVersionId,
      spaceId,
      includeConstituentAccess: true,
      ...(transmittal?.transmittalId ? { transmittalId: transmittal.transmittalId } : {}),
    });
    return response.data;
  } catch (error: unknown) {
    Sentry.captureException(error);
    return { status: false, errorMessage: "isFile403 request failed" };
  }
}

/**
 * Ids worth loading model data for. Falls back to the un-gated list when the result
 * carries no `files` — the check failed, or an older server ignored
 * includeConstituentAccess. The server refuses those fetches itself, so this gate
 * only exists to avoid pointless requests.
 */
export function viewableFileVersionIdsFrom(
  access: FileAccessResult | undefined,
  filesData: Record<string, any> | undefined,
  fileVersionId: string,
): string[] {
  return access?.status === true && access.files
    ? (access.viewableFileVersionIds ?? [])
    : resolveFileVersionIds(filesData, fileVersionId);
}
